import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "../console/api";
import { SECTIONS } from "./hubSections";

const STAMP = "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/ptv4fnbg_file_00000000ae9c7207a6fe6772e4a14ae5.png";
const AUBERGINE = "#3E1F46";
const PLUM = "#5B2A4F";

export default function AdminHub() {
  const [counts, setCounts] = useState(null);
  const [pay, setPay] = useState(null);

  useEffect(() => {
    api.get("/console/dashboard").then((r) => setCounts(r.data)).catch(() => {});
    api.get("/console/payments/overview").then((r) => setPay(r.data)).catch(() => {});
  }, []);

  const today = new Date().toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="admin-hub" className="max-w-5xl">
      {/* Welcome banner */}
      <header className="flex items-center gap-5 mb-8">
        <img src={STAMP} alt="Heritage Dahlias" className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow" data-testid="hub-stamp" />
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Heritage Admin</div>
          <h1 className="font-serif text-4xl md:text-5xl mt-1" style={{ color: AUBERGINE }}>Welcome back.</h1>
          <p className="text-sm italic mt-1 text-[#6E5849]">{today} · one thing at a time, you&apos;ve got this.</p>
        </div>
      </header>

      {/* Today at a glance */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="hub-glance">
        <Glance label="Owed to you" value={pay ? `NZ$${Number(pay.tiles.total_owed).toFixed(0)}` : "…"} to="/admin/payments" hot={pay?.tiles?.total_owed > 0} testid="glance-owed" />
        <Glance label="Unpaid orders" value={pay ? pay.tiles.unpaid_orders : "…"} to="/admin/payments" testid="glance-unpaid" />
        <Glance label="To pack" value={counts ? (counts.assigned ?? 0) : "…"} to="/admin/orders/packing" testid="glance-packing" />
        <Glance label="To ship" value={counts ? (counts.shipping ?? 0) : "…"} to="/admin/orders/to-ship" testid="glance-ship" />
      </section>

      {/* The four hubs , each lands on its own sub-dashboard */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="hub-tiles">
        {Object.entries(SECTIONS).map(([key, s]) => {
          const Icon = s.Icon;
          const badges = badgesFor(key, counts, pay);
          return (
            <Link
              key={key}
              to={`/admin/hub/${key}`}
              data-testid={`hub-tile-${key}`}
              className="group relative block rounded-md p-6 aspect-square flex flex-col items-center justify-center text-center transition-transform hover:-translate-y-1 hover:shadow-lg"
              style={{ backgroundColor: s.tile }}
            >
              {badges.length > 0 && (
                <div className="absolute top-2.5 right-2.5 flex flex-col items-end gap-1" data-testid={`hub-badges-${key}`}>
                  {badges.map((b) => (
                    <span
                      key={b.label}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap"
                      style={{ backgroundColor: b.tone, color: "#FBF6E9" }}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              )}
              <Icon className="w-16 h-16" strokeWidth={1.2} style={{ color: AUBERGINE }} />
              <div className="font-serif text-2xl mt-4" style={{ color: AUBERGINE }}>{s.label}</div>
              <div className="text-xs mt-1 opacity-70" style={{ color: AUBERGINE }}>{s.blurb}</div>
            </Link>
          );
        })}
      </section>
    </div>
  );
}

function badgesFor(key, counts, pay) {
  const out = [];
  if (key === "orders") {
    const overdue5 = pay?.tiles?.overdue_5d || 0;
    if (overdue5) out.push({ label: `⏰ ${overdue5} overdue 5d+`, tone: "#A33030" });
    const unpaid = pay?.tiles?.unpaid_orders || 0;
    const toPack = counts?.assigned || 0;
    const toShip = counts?.shipping || 0;
    const complaints = counts?.open_complaints || 0;
    if (unpaid) out.push({ label: `${unpaid} unpaid`, tone: "#A33030" });
    if (toPack) out.push({ label: `${toPack} to pack`, tone: "#C49247" });
    if (toShip) out.push({ label: `${toShip} to ship`, tone: "#5B2A4F" });
    if (complaints) out.push({ label: `${complaints} complaint${complaints === 1 ? "" : "s"}`, tone: "#A33030" });
    if (!out.length && counts) out.push({ label: "all clear ✓", tone: "#2F4A2A" });
  }
  if (key === "customers" && counts) {
    out.push({ label: `${counts.customers || 0} customers`, tone: "#5B2A4F" });
    if (counts.hq_customers) out.push({ label: `${counts.hq_customers} in HQ`, tone: "#C49247" });
  }
  if (key === "operations" && counts) {
    out.push({ label: `${counts.inventory || 0} varieties live`, tone: "#5B2A4F" });
  }
  return out;
}

function Glance({ label, value, to, hot = false, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="rounded-md border p-4 bg-white hover:shadow-md transition-shadow flex items-center justify-between gap-2"
      style={{ borderColor: hot ? "#A33030" : "#E0D1C8" }}
    >
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: hot ? "#A33030" : PLUM }}>{label}</div>
        <div className="font-serif text-3xl mt-0.5" style={{ color: AUBERGINE }}>{value}</div>
      </div>
      <ChevronRight className="w-4 h-4 opacity-40" />
    </Link>
  );
}
